import type { ScaleMode } from "../render/index";
import { JD_MAX, JD_MIN, clampJd, type Clock } from "../sim/clock";
import type { Store } from "./store";

export interface HashState {
  focus?: string;
  scale?: ScaleMode;
  jd?: number;
  /** Set by `stats=1`, so the readout is up from the first frame without touching the menu. */
  stats?: boolean;
}

export function parseHash(hash: string, validIds: ReadonlySet<string>): HashState {
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  const out: HashState = {};
  const focus = params.get("focus");
  if (focus && validIds.has(focus)) out.focus = focus;
  const scale = params.get("scale");
  if (scale === "visual" || scale === "true") out.scale = scale;
  const jd = Number(params.get("jd"));
  if (params.has("jd") && jd >= JD_MIN && jd <= JD_MAX) out.jd = jd;
  if (params.get("stats") === "1") out.stats = true;
  return out;
}

/** The date as the hash carries it: four decimals. */
export function roundJd(jd: number): number {
  return Number(jd.toFixed(4));
}

export function serializeHash(focus: string, scale: ScaleMode, jd: number, stats: boolean): string {
  // kept while on, so a reload still measures the page from its first frame
  return (
    `#focus=${encodeURIComponent(focus)}&scale=${scale}&jd=${roundJd(jd)}` +
    (stats ? "&stats=1" : "")
  );
}

/**
 * The dates the app recently wrote to the hash. A typed address still carries one
 * of these, so only a date not in here is a request to jump in time.
 */
export class WrittenDates {
  private readonly seen = new Set<number>();

  /** The hash is rewritten every second, so the default covers about five minutes of typing. */
  constructor(private readonly cap = 300) {}

  add(jd: number): void {
    this.seen.delete(jd);
    this.seen.add(jd);
    if (this.seen.size > this.cap) this.seen.delete(this.seen.values().next().value!);
  }

  has(jd: number): boolean {
    return this.seen.has(jd);
  }
}

/** The parts of window the adapter touches, so tests can fake them without a DOM. */
export type AddressWindow = Pick<Window, "location" | "history" | "addEventListener">;

/** Where the hash is read and written. */
export interface Address {
  read(): string;
  /** Rewrites the hash without a history entry or a hashchange event. */
  replace(hash: string): void;
  /** Called with the new hash on every edit, back and forward. */
  onChange(fn: (hash: string) => void): void;
}

export function browserAddress(win: AddressWindow): Address {
  return {
    read: () => win.location.hash,
    replace: (hash) => win.history.replaceState(null, "", hash),
    // The event's own URL, not location.hash: the once-a-second rewrite can
    // land between an edit and its event and would put the old focus back.
    onChange: (fn) => win.addEventListener("hashchange", (e) => fn(new URL(e.newURL).hash)),
  };
}

export interface HashSyncDeps {
  store: Store;
  clock: Clock;
  /** Valid body ids; any other focus in the hash is ignored. */
  ids: ReadonlySet<string>;
  address: Address;
  /** Minimum time between rewrites, ms. */
  intervalMs?: number;
}

/**
 * Applies the address now and on every edit, and rewrites it from the view
 * state at most once per interval when ticked from the frame loop.
 */
export function createHashSync({ store, clock, ids, address, intervalMs = 1000 }: HashSyncDeps): {
  tick(now: number): void;
} {
  const written = new WrittenDates();
  const apply = (raw: string): void => {
    const hash = parseHash(raw, ids);
    if (hash.jd !== undefined && !written.has(hash.jd)) clock.jd = clampJd(hash.jd);
    store.set({
      focus: hash.focus ?? "sun",
      scale: hash.scale ?? "visual",
      stats: hash.stats ?? false,
    });
  };
  apply(address.read());
  address.onChange(apply);

  let lastWrite = 0;
  return {
    tick(now) {
      if (now - lastWrite <= intervalMs) return;
      lastWrite = now;
      const { focus, scale, stats } = store.state;
      address.replace(serializeHash(focus, scale, clock.jd, stats));
      written.add(roundJd(clock.jd));
    },
  };
}
